import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import RangeSlider from 'react-bootstrap-range-slider';
import translate from '../../../commonComponents/translate';
import CategoryProp from '../../academy/propTypes/categoryProp';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import { displayMoney, SESSION_STATUS, SESSION_STATUSES, SESSION_TYPES } from '../../../services/utilityFunctions';
import CurrencyProp from '../../profile/propTypes/currencyProp';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import PriceRangeProp from '../../academy/propTypes/PriceRangeProp';

const FILTERS = {
  CATEGORY: 'category',
  LANGUAGE: 'language',
  SKILL: 'hardSkill',
  SESSION_STATUS: 'status',
  SESSION_TYPE: 'type',
};

function SessionFilters(props) {
  const {
    t,
    categories,
    languages,
    currencies,
    selected,
    onChange,
    onPriceChange,
    priceRange,
  } = props;
  const { loggedUser } = useContext(LoggedUserContext);

  const [openItems, setOpenItems] = useState([
    FILTERS.CATEGORY,
    FILTERS.LANGUAGE,
    FILTERS.SESSION_STATUS,
    FILTERS.SESSION_TYPE,
  ]);
  const toggleItem = filter => () =>
    setOpenItems(
      open =>
        open.includes(filter)
          ? open.filter(f => f !== filter)
          : open.concat(filter),
    );

  const handleChange = value => evt => {
    evt.preventDefault();
    onChange(value);
  };

  const handleCertificateToggle = () => {
    onChange({ certificate: selected.certificate ? undefined : true });
  };

  const sessionStatusText = sessionStatus => typeof sessionStatus === 'string' ? t(`access_session.status.${sessionStatus}`) : t('all');
  const sessionTypeText = sessionType => typeof sessionType === 'string' ? t(`sessions_${sessionType}`) : t('all');

  const allCategories = [{ _id: '', name: t('all') }].concat(categories);
  const allLanguages = [{ code: '', native: t('all') }].concat(languages);
  const allStatuses = [''].concat(SESSION_STATUSES.filter(v => v === SESSION_STATUS.FINISHED || v === SESSION_STATUS.ONGOING || v === SESSION_STATUS.UNSTARTED));
  const allTypes = [''].concat(SESSION_TYPES);

  const userCurrencyCode = priceRange.currency;
  const userCurrency = currencies.find(c => c.code === userCurrencyCode) || {};

  return (
    <div className="m-portlet m-portlet--rounded m-portlet--rounded-force">
      <div className="m-portlet__body m--padding-top-5 m--padding-bottom-20 shop-sidebar">
        <div className="row">
          <div className="col-lg-12">
            <br />
            <div className="shop-sidebar-categories">
              <ul className="list-group">
                <h5>{t('academy_filters')}</h5>
                <li
                  key={FILTERS.CATEGORY}
                  className={cx(
                    'list-group-item',
                    'd-flex',
                    'justify-content-between',
                    'align-items-center',
                    { selected: openItems.includes(FILTERS.CATEGORY) },
                  )}
                  onClick={toggleItem(FILTERS.CATEGORY)}
                >
                  {t('academy_category')}
                </li>
                <ul
                  key={`${FILTERS.CATEGORY}-list`}
                  className="sub-list"
                  type="disc"
                >
                  {allCategories.map(category => (
                    <li key={category._id}>
                      <a
                        href=""
                        className={cx({
                          selected: category._id === selected.category,
                        })}
                        onClick={handleChange({ category: category._id })}
                      >
                        {category.name}
                      </a>
                    </li>
                  ))}
                </ul>
                <li
                  key={FILTERS.LANGUAGE}
                  className={cx(
                    'list-group-item',
                    'd-flex',
                    'justify-content-between',
                    'align-items-center',
                    { selected: openItems.includes(FILTERS.LANGUAGE) },
                  )}
                  onClick={toggleItem(FILTERS.LANGUAGE)}
                >
                  {t('academy_language')}
                </li>
                <ul
                  key={`${FILTERS.LANGUAGE}-list`}
                  className="sub-list"
                  type="disc"
                >
                  {allLanguages.map(language => (
                    <li key={language.code}>
                      <a
                        href=""
                        className={cx({
                          selected: language.code === selected.language,
                        })}
                        onClick={handleChange({ language: language.code })}
                      >
                        {language.native}
                      </a>
                    </li>
                  ))}
                </ul>
                <li
                  key={FILTERS.SESSION_STATUS}
                  className={cx(
                    'list-group-item',
                    'd-flex',
                    'justify-content-between',
                    'align-items-center',
                    { selected: openItems.includes(FILTERS.SESSION_STATUS) },
                  )}
                  onClick={toggleItem(FILTERS.SESSION_STATUS)}
                >
                  {t('access_session.status')}
                </li>
                <ul
                  key={`${FILTERS.SESSION_STATUS}-list`}
                  className="sub-list"
                  type="disc"
                >
                  {
                   allStatuses.map(status => (
                    <li key={status}>
                      <a
                        href=""
                        className={cx({
                          selected: status === selected.status,
                        })}
                        onClick={handleChange({ status })}
                      >
                        { status === '' ? t('all') : sessionStatusText(status)}
                      </a>
                    </li>
                  ))}
                </ul>

                <li
                  key={FILTERS.SESSION_TYPE}
                  className={cx(
                    'list-group-item',
                    'd-flex',
                    'justify-content-between',
                    'align-items-center',
                    { selected: openItems.includes(FILTERS.SESSION_TYPE) },
                  )}
                  onClick={toggleItem(FILTERS.SESSION_TYPE)}
                >
                  {t('access_session.type')}
                </li>
              <ul
                key={`${FILTERS.SESSION_TYPE}-list`}
                className="sub-list"
                type="disc"
              >
                {
                  allTypes.map(session_type => (
                    <li key={session_type}>
                      <a
                        href=""
                        className={cx({
                          selected: session_type === selected.type,
                        })}
                        onClick={handleChange({ type: session_type })}
                      >
                        { session_type === '' ? t('all') : sessionTypeText(session_type)}
                      </a>
                    </li>
                  ))}
                </ul>
              </ul>
            </div>
            <div className="shop-sidebar-price">
              <h5>{t('academy_max_price')}</h5>
              <RangeSlider
                variant="primary"
                tooltip="off"
                max={priceRange.max}
                min={priceRange.min}
                value={selected.price}
                onChange={({ target }) => onPriceChange('price')(parseInt(target.value, 10))}
              />
              <div className="price-range-values">
                <span className="initial-value">{t('free')}</span>
                <span className="current-value">
                  {selected.price
                    ? displayMoney(selected.price, userCurrency.symbol)
                    : t('free')}
                </span>
                <span className="final-value">
                  {displayMoney(priceRange.max, userCurrency.symbol)}
                </span>
              </div>
              <br />
            </div>
            <div className="shop-sidebar-size">
              <h5>{t('academy_certificate')}</h5>
              <form className="form">
                <div className="form-group row">
                  <div className="col-9 col-form-label">
                    <div className="checkbox-list">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={selected.certificate === true}
                          onChange={handleCertificateToggle}
                        />
                        {t('academy_required')}
                        <span />
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SessionFilters.propTypes = {
  t: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  selected: PropTypes.shape({
    category: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
    certificate: PropTypes.bool,
    price: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onPriceChange: PropTypes.func.isRequired,
  priceRange: PropTypes.shape(PriceRangeProp.propType).isRequired,
};

export default translate(SessionFilters);
